import { getSession, trpc, useSession } from '@tg/app-core';
import { useEffect, useRef, useState } from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useMe, useToday } from './lib/data';
import { rememberPath, resumeTarget, touchLast } from './lib/nav';
import { lsGet, lsSet } from './lib/storage';
import { initPush } from './lib/push';
import { applyTheme, getTheme, type Theme } from './lib/theme';
import { Code, Phone } from './screens/Auth';
import { Feed } from './screens/Feed';
import { Home } from './screens/Home';
import { Profile } from './screens/Profile';
import { Settings } from './screens/Settings';
import { SPLASH_TOTAL, Splash } from './screens/Splash';
import { WizData, WizGoals } from './screens/Wizard';
import { Workout } from './screens/Workout';
import { AchQueueHost, Howto, inTelegram } from './ui/FirstSteps';
import { enqueue } from './ui/achQueue';
import { toast, Toasts } from './ui/overlay';

/* Роутинг — HashRouter: в Capacitor страница грузится с capacitor://localhost
   (или file://), сервера с fallback на index.html нет — адрес в хеше работает
   без настройки сервера, в PWA, в WebView и на любом статическом хостинге,
   и совпадает со схемой адресов прототипа (#/screen). */

const PUBLIC = new Set(['/phone', '/code']);
const screenId = (p: string) =>
  p === '/' ? 'home' : p.startsWith('/day/') ? 'workout' : p.startsWith('/wiz/data') ? 'wiz-data' : p.startsWith('/wiz/goals') ? 'wiz-goals' : p.slice(1) || 'home';
const TITLES: Record<string, string> = {
  phone: 'Вход',
  code: 'Код из СМС',
  'wiz-data': 'Данные',
  'wiz-goals': 'Цели',
  home: 'Главная',
  workout: 'Тренировка',
  feed: 'Лента событий',
  profile: 'Профиль',
  settings: 'Настройки',
};

export function App() {
  return (
    <HashRouter>
      <Shell />
    </HashRouter>
  );
}

function Shell() {
  const loc = useLocation();
  const nav = useNavigate();
  const navType = useNavigationType();
  const session = useSession();
  const me = useMe();
  const today = useToday();
  const path = loc.pathname;
  const id = screenId(path);

  /* ── S1: заставка — только когда приложение открыли впервые на этом устройстве и вход ещё не выполнен
     (идёт первая загрузка). Уже вошедший клиент с данными в памяти попадает сразу в приложение.
     Кончается, когда прошло 7 с и данные загрузились (или нет сети), раньше — по тапу ── */
  /* В Telegram заставки нет: сначала «Откройте в браузере» (R0), человек всё равно уйдёт в браузер */
  const [splash, setSplash] = useState(() => !session && !lsGet('splashSeen', false) && !inTelegram());
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setElapsed(true), SPLASH_TOTAL);
    return () => clearTimeout(t);
  }, []);
  const ready = !session || !!me.data || me.isError || me.fetchStatus === 'paused';
  /* Ачивки «за первое», довыданные лениво на me() (данные мог внести тренер) — в очередь (ACH-2) */
  useEffect(() => {
    me.data?.newAch?.forEach((id) => enqueue({ kind: 'ach', id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.newAch]);
  const cold = useRef(true);
  const endSplash = () => {
    setSplash(false);
    lsSet('splashSeen', true);
    /* Холодный старт: та же логика возврата, что из фона (CLI-6) */
    if (cold.current && session) {
      cold.current = false;
      const t = resumeTarget(today);
      if (t && t !== path && !PUBLIC.has(path) && !path.startsWith('/wiz')) nav(t, { replace: true });
    }
  };
  useEffect(() => {
    if (splash && elapsed && ready) endSplash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splash, elapsed, ready]);
  /* Без заставки — та же логика холодного старта (CLI-6), как только данные на месте */
  useEffect(() => {
    if (splash || !cold.current || !session || !ready) return;
    cold.current = false;
    const t = resumeTarget(today);
    if (t && t !== path && !PUBLIC.has(path) && !path.startsWith('/wiz')) nav(t, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splash, session, ready]);

  /* ── CLI-6: возврат из фона. Шла тренировка — она; иначе <30 мин — тот же экран, больше — главная ── */
  useEffect(() => {
    if (session && !PUBLIC.has(path) && !path.startsWith('/wiz')) rememberPath(path);
  }, [path, session]);
  useEffect(() => {
    const on = () => {
      if (document.visibilityState === 'hidden') return touchLast();
      if (!getSession()) return;
      const p = window.location.hash.replace(/^#/, '') || '/';
      if (PUBLIC.has(p) || p.startsWith('/wiz')) return;
      const t = resumeTarget(today);
      if (t && t !== p) nav(t, { replace: true });
    };
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, [today, nav]);

  /* Тема с сервера: выбрана на другом устройстве — применяем */
  const srvTheme = (me.data?.settings as { theme?: Theme } | undefined)?.theme;
  useEffect(() => {
    if (srvTheme && srvTheme !== getTheme()) applyTheme(srvTheme);
  }, [srvTheme]);

  /* Ссылка тренера открыта уже вошедшим (main.tsx отложил код в pendingInvite,
     не погнал на экран входа): привязываем тренера и сообщаем тостом. */
  const utils = trpc.useUtils();
  const join = trpc.clientApp.joinByInvite.useMutation();
  useEffect(() => {
    if (!session || !me.data) return;
    const code = lsGet<string | undefined>('pendingInvite', undefined);
    if (!code) return;
    lsSet('pendingInvite', undefined);
    join.mutate(
      { code },
      {
        onSuccess: (r) => {
          toast(r.linked ? `Тренер «${r.trainerName}» добавлен` : `У вас уже есть тренер — «${r.trainerName}» не добавлен`);
          if (r.linked) utils.clientApp.me.invalidate();
        },
        onError: (e) => toast(e.message || 'Ссылка не сработала'),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, !!me.data]);

  /* Пуши (iOS из стора): после входа и мастера — разрешение и токен; нажатие на пуш открывает свой экран */
  const onboarded = !!(session && me.data?.onboardedAt);
  useEffect(() => {
    if (onboarded)
      initPush(
        (p) => nav(p),
        () => utils.clientApp.feed.invalidate(),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarded]);

  useEffect(() => {
    document.title = (TITLES[id] || 'Тренерграм') + ' · Тренерграм';
  }, [id]);

  /* ── доступ: без входа — номер телефона; аккаунт не создан до конца — мастер ── */
  let redirect: string | null = null;
  if (!session && !PUBLIC.has(path)) redirect = '/phone';
  else if (session && PUBLIC.has(path)) redirect = '/';
  else if (session && me.data && !me.data.onboardedAt && !path.startsWith('/wiz')) redirect = '/wiz/data';
  else if (session && me.data?.onboardedAt && path.startsWith('/wiz')) redirect = '/';

  const dir = navType === 'POP' ? 'back' : 'enter';
  return (
    <div className="dev" id="dev">
      {redirect ? (
        <Navigate to={redirect} replace />
      ) : (
        <main key={path} className={`scr scr-${id} ${dir}`}>
          <Routes>
            <Route path="/phone" element={<Phone />} />
            <Route path="/code" element={<Code />} />
            <Route path="/wiz/data" element={<WizData />} />
            <Route path="/wiz/goals" element={<WizGoals />} />
            <Route path="/" element={<Home />} />
            <Route path="/day/:date" element={<Workout />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      )}
      {splash ? <Splash onTap={endSplash} /> : null}
      <Howto />
      <AchQueueHost />
      <Toasts />
    </div>
  );
}

